import React, { Key, useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import { Alert, Flex, Input, message, Modal, ModalProps, Pagination, Table, Transfer } from 'antd';
import { documentId } from 'firebase/firestore';
import { ICompany } from '@leedpulse-monorepo/interfaces';
import CompaniesContext from '../../../contexts/data/companies/CompaniesContext';
import { TableRowSelection } from 'antd/es/table/interface';
import { TransferItem } from 'antd/es/transfer';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import searchSubscriptions from '../../../../domain/use_cases/subscriptions/searchSubscriptions';
import getSubscriptionsByConstraints, {
    SUBSCRIPTION_FIRESTORE_IN_MAX,
} from '../../../../domain/use_cases/subscriptions/getSubscriptionsByConstraints';
import { ISubscriptionView } from '../../../../domain/interfaces/ISubscriptionView';
import { EProductType } from '@leedpulse-monorepo/enums';
import updateCompanySubscriptions from '../../../../domain/use_cases/companies/updateCompanySubscriptions';

import styles from './CompanyManageSubscriptionsModal.module.css';

interface CompanyManageSubscriptionsModalProps extends ModalProps {
    onSuccess?: () => void | Promise<void>;
}

async function fetchSubscriptionNamesByIds(ids: string[]): Promise<Record<string, string>> {
    const names: Record<string, string> = {};
    for (let i = 0; i < ids.length; i += SUBSCRIPTION_FIRESTORE_IN_MAX) {
        const chunk = ids.slice(i, i + SUBSCRIPTION_FIRESTORE_IN_MAX);
        const { subscriptions } = await getSubscriptionsByConstraints(
            [
                { field: documentId(), op: 'in', value: chunk },
                { field: documentId(), op: 'orderBy', value: 'asc' },
            ],
            { pageSize: chunk.length }
        );
        subscriptions.forEach((sub) => {
            names[sub.id] = sub.name || '';
        });
    }
    return names;
}

export default function CompanyManageSubscriptionsModal({
    onSuccess,
    ...modalProps
}: CompanyManageSubscriptionsModalProps) {
    const companiesContext = useContext(CompaniesContext);
    const authContext = useContext(AuthenticationContext);

    const companyDoc = companiesContext.selected as ICompany;

    // Subscription ids linked to the company (Transfer left / source panel).
    const [linkedKeys, setLinkedKeys] = useState<string[]>([]);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [queryDraft, setQueryDraft] = useState('');
    const [queryDebounced, setQueryDebounced] = useState('');
    const [catalogPage, setCatalogPage] = useState(0);
    const [catalogPageSize, setCatalogPageSize] = useState(10);
    const [companyPage, setCompanyPage] = useState(1);
    const [companyPageSize, setCompanyPageSize] = useState(10);
    const [catalogHits, setCatalogHits] = useState<ISubscriptionView[]>([]);
    const [catalogNbHits, setCatalogNbHits] = useState(0);
    const [isCatalogLoading, setIsCatalogLoading] = useState(false);
    const [subscriptionNameById, setSubscriptionNameById] = useState<Record<string, string>>({});
    const [companyPageItems, setCompanyPageItems] = useState<TransferItem[]>([]);
    const [companyFilteredCount, setCompanyFilteredCount] = useState(0);
    const [isCompanyLoading, setIsCompanyLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const companyPageFetchGenRef = useRef(0);
    const subscriptionNameByIdRef = useRef<Record<string, string>>({});
    const hitByIdRef = useRef<Map<string, ISubscriptionView>>(new Map());
    const catalogTargetKeysRef = useRef<string[]>([]);

    const companySubscriptionsById: Record<string, { updated: number }> =
        (companyDoc.id_subscriptions ?? {}) as Record<string, { updated: number }>;

    const companySubscriptionIdsKey = useMemo(
        () => Object.keys(companySubscriptionsById).sort().join(','),
        [companyDoc.id_subscriptions]
    );

    const sortedLinkedKeys = useMemo(() => [...linkedKeys].sort(), [linkedKeys]);
    const linkedKeysKey = useMemo(() => sortedLinkedKeys.join(','), [sortedLinkedKeys]);
    const linkedKeySet = useMemo(() => new Set(linkedKeys), [linkedKeys]);

    const companyPageIdSet = useMemo(
        () => new Set(companyPageItems.map((item) => String(item.key))),
        [companyPageItems]
    );

    const hitById = useMemo(() => {
        const m = new Map<string, ISubscriptionView>();
        catalogHits.forEach((h) => m.set(h.objectID, h));
        return m;
    }, [catalogHits]);

    const catalogNameById = useMemo(() => {
        const names: Record<string, string> = {};
        catalogHits.forEach((hit) => {
            if (hit.name) names[hit.objectID] = hit.name;
        });
        return names;
    }, [catalogHits]);

    // Transfer targetKeys = catalog panel (right); linked keys stay on the left.
    const catalogTargetKeys = useMemo(
        () => catalogHits.map((hit) => hit.objectID).filter((id) => !linkedKeySet.has(id)),
        [catalogHits, linkedKeySet]
    );

    useEffect(() => {
        catalogTargetKeysRef.current = catalogTargetKeys;
    }, [catalogTargetKeys]);

    const transferTitles = useMemo(
        () => [
            `Abonnements de ${companyDoc.name} (${companyFilteredCount})`,
            `Abonnements Leedflow (${catalogNbHits})`,
        ],
        [companyDoc.name, companyFilteredCount, catalogNbHits]
    );

    const getSubscriptionDisplayName = useCallback(
        (id: string, catalogNameById: Record<string, string>) =>
            subscriptionNameByIdRef.current[id] ||
            catalogNameById[id] ||
            hitByIdRef.current.get(id)?.name ||
            '',
        []
    );

    const dataSource: TransferItem[] = useMemo(() => {
        const byKey = new Map<string, TransferItem>();
        const searchQuery = queryDebounced.trim().toLowerCase();

        catalogHits.forEach((hit) => {
            byKey.set(hit.objectID, {
                key: hit.objectID,
                title: hit.name || hit.objectID,
                description: hit.name || hit.objectID,
            });
        });

        // Keep unlinked search matches visible on the catalog side when off the current Algolia page.
        // Lit le state `subscriptionNameById` et le memo `hitById` plutot que
        // leurs miroirs en ref : meme contenu (ecrits ensemble dans
        // `mergeSubscriptionNames`), mais ce sont des deps du memo, donc il
        // recalcule quand les noms arrivent. Les refs, elles, servent aux
        // callbacks async qui ne peuvent pas dependre du rendu.
        if (searchQuery) {
            Object.entries(subscriptionNameById).forEach(([id, name]) => {
                if (linkedKeySet.has(id) || byKey.has(id)) return;
                if (!name.toLowerCase().includes(searchQuery)) return;
                byKey.set(id, { key: id, title: name, description: name });
            });
        }

        linkedKeys.forEach((id) => {
            if (!companyPageIdSet.has(id) && !hitById.has(id)) return;

            const name =
                subscriptionNameById[id] ||
                catalogNameById[id] ||
                hitById.get(id)?.name ||
                id;
            byKey.set(id, {
                key: id,
                title: name,
                description: name,
            });
        });

        return Array.from(byKey.values());
    }, [
        catalogHits,
        catalogNameById,
        companyPageIdSet,
        hitById,
        linkedKeySet,
        linkedKeys,
        queryDebounced,
        subscriptionNameById,
    ]);

    const mergeSubscriptionNames = useCallback((names: Record<string, string>) => {
        const entries = Object.entries(names).filter(([, name]) => name.length > 0);
        if (entries.length === 0) return;

        const patch = Object.fromEntries(entries);
        subscriptionNameByIdRef.current = {
            ...subscriptionNameByIdRef.current,
            ...patch,
        };
        setSubscriptionNameById((prev) => ({ ...prev, ...patch }));
    }, []);

    const resolveCompanyCandidateIds = useCallback(
        async (sortedIds: string[], searchQuery: string, catalogNameById: Record<string, string>) => {
            const q = searchQuery.trim().toLowerCase();
            if (!q) return sortedIds;

            mergeSubscriptionNames(catalogNameById);

            const missing = sortedIds.filter(
                (id) => getSubscriptionDisplayName(id, catalogNameById) === ''
            );
            if (missing.length > 0) {
                const stubs = await fetchSubscriptionNamesByIds(missing);
                mergeSubscriptionNames(stubs);
            }

            return sortedIds.filter((id) =>
                getSubscriptionDisplayName(id, catalogNameById).toLowerCase().includes(q)
            );
        },
        [getSubscriptionDisplayName, mergeSubscriptionNames]
    );

    const fetchCompanySubscriptionsPage = useCallback(
        async (
            page: number,
            candidateIds: string[],
            catalogNameById: Record<string, string>,
            gen: number
        ) => {
            try {
                const start = (page - 1) * companyPageSize;
                const pageIds = candidateIds.slice(start, start + companyPageSize);

                if (pageIds.length === 0) {
                    if (gen !== companyPageFetchGenRef.current) return;
                    setCompanyPageItems([]);
                    return;
                }

                const missingOnPage = pageIds.filter(
                    (id) => subscriptionNameByIdRef.current[id] === undefined
                );
                const namesOnPage =
                    missingOnPage.length > 0
                        ? await fetchSubscriptionNamesByIds(missingOnPage)
                        : {};

                if (gen !== companyPageFetchGenRef.current) return;

                mergeSubscriptionNames(namesOnPage);

                setCompanyPageItems(
                    pageIds.map((id) => {
                        const name = getSubscriptionDisplayName(id, catalogNameById) || id;
                        return {
                            key: id,
                            title: name,
                            description: name,
                        };
                    })
                );
            } catch (e) {
                if (gen !== companyPageFetchGenRef.current) return;
                console.error('[CompanyManageSubscriptionsModal] fetch company page error:', e);
                setCompanyPageItems([]);
            }
        },
        [companyPageSize, getSubscriptionDisplayName, mergeSubscriptionNames]
    );

    const handleTransferChange = useCallback(
        (nextCatalogTargetKeys: Key[]) => {
            const nextCatalog = new Set(nextCatalogTargetKeys.map(String));
            const prevCatalog = new Set(catalogTargetKeysRef.current);
            const namesPatch: Record<string, string> = {};

            prevCatalog.forEach((id) => {
                if (nextCatalog.has(id)) return;
                const fromCatalog = hitByIdRef.current.get(id)?.name;
                if (fromCatalog) namesPatch[id] = fromCatalog;
            });

            mergeSubscriptionNames(namesPatch);

            setLinkedKeys((prevLinked) => {
                const nextLinked = new Set(prevLinked);
                prevCatalog.forEach((id) => {
                    if (!nextCatalog.has(id)) nextLinked.add(id);
                });
                nextCatalog.forEach((id) => {
                    if (!prevCatalog.has(id)) nextLinked.delete(id);
                });
                return [...nextLinked];
            });
        },
        [mergeSubscriptionNames]
    );

    const handleSubmit = async () => {
        if (!authContext.authentication) return;

        const nextIdSubscriptions: Record<string, { updated: number }> = {};
        linkedKeys.forEach((id) => {
            nextIdSubscriptions[id] = {
                updated: companySubscriptionsById[id]?.updated ?? Date.now(),
            };
        });

        setIsSubmitting(true);
        try {
            await updateCompanySubscriptions(
                {
                    company_id: companyDoc.id,
                    id_subscriptions: nextIdSubscriptions,
                },
                authContext.authentication
            );
            await onSuccess?.();
        } catch (e) {
            message.error(
                (e as { message?: string })?.message ||
                'Erreur lors de la mise à jour des abonnements'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (!modalProps.open) return;
        companyPageFetchGenRef.current += 1;
        subscriptionNameByIdRef.current = {};
        // L'effet est couple a une ref (garde de pagination, generation de fetch) :
        // ecrire cette ref pendant le rendu serait une autre violation. On garde l'effet.
        // eslint-disable-next-line react/set-state-in-effect
        setSubscriptionNameById({});
        setCompanyPageItems([]);
        setCompanyFilteredCount(0);
        setLinkedKeys(Object.keys(companySubscriptionsById));
        setSelectedKeys([]);
        setQueryDraft('');
        setQueryDebounced('');
        setCatalogPage(0);
        setCatalogPageSize(10);
        setCompanyPage(1);
        setCompanyPageSize(10);
    }, [companyDoc.id, companySubscriptionIdsKey, modalProps.open]);

    useEffect(() => {
        const t = window.setTimeout(() => setQueryDebounced(queryDraft.trim()), 350);
        return () => window.clearTimeout(t);
    }, [queryDraft]);

    useEffect(() => {
        if (!modalProps.open) return;

        const catalogNameById: Record<string, string> = {};
        catalogHits.forEach((hit) => {
            catalogNameById[hit.objectID] = hit.name;
        });

        async function loadCompanyList() {
            if (sortedLinkedKeys.length === 0) {
                setCompanyFilteredCount(0);
                setCompanyPageItems([]);
                setIsCompanyLoading(false);
                return;
            }

            const gen = ++companyPageFetchGenRef.current;
            setIsCompanyLoading(true);

            try {
                const candidateIds = await resolveCompanyCandidateIds(
                    sortedLinkedKeys,
                    queryDebounced,
                    catalogNameById
                );
                if (gen !== companyPageFetchGenRef.current) return;

                setCompanyFilteredCount(candidateIds.length);

                const maxPage = Math.max(1, Math.ceil(candidateIds.length / companyPageSize));
                const page = Math.min(companyPage, maxPage);
                if (page !== companyPage) setCompanyPage(page);

                await fetchCompanySubscriptionsPage(page, candidateIds, catalogNameById, gen);
            } catch (e) {
                if (gen !== companyPageFetchGenRef.current) return;
                console.error('[CompanyManageSubscriptionsModal] load company list error:', e);
                setCompanyFilteredCount(0);
                setCompanyPageItems([]);
            } finally {
                if (gen === companyPageFetchGenRef.current) setIsCompanyLoading(false);
            }
        }

        void loadCompanyList();
    }, [
        companyPage,
        companyPageSize,
        fetchCompanySubscriptionsPage,
        modalProps.open,
        queryDebounced,
        resolveCompanyCandidateIds,
        sortedLinkedKeys,
        linkedKeysKey,
        catalogHits,
    ]);

    useEffect(() => {
        async function fetchCatalog() {
            if (!modalProps.open) return;
            setIsCatalogLoading(true);
            try {
                const res = await searchSubscriptions(
                    authContext.authentication,
                    queryDebounced,
                    catalogPage,
                    catalogPageSize,
                    { products: [EProductType.LEEDFLOW] },
                );
                setCatalogHits(res.hits);
                setCatalogNbHits(res.nbHits);
            } catch (e) {
                console.error('[CompanyManageSubscriptionsModal] searchSubscriptions error:', e);
                setCatalogHits([]);
                setCatalogNbHits(0);
            } finally {
                setIsCatalogLoading(false);
            }
        }
        void fetchCatalog();
    }, [authContext.authentication, catalogPage, catalogPageSize, modalProps.open, queryDebounced]);

    useEffect(() => {
        hitByIdRef.current = hitById;
        // L'effet est couple a une ref (garde de pagination, generation de fetch) :
        // ecrire cette ref pendant le rendu serait une autre violation. On garde l'effet.
        // eslint-disable-next-line react/set-state-in-effect
        mergeSubscriptionNames(catalogNameById);
    }, [catalogNameById, hitById, mergeSubscriptionNames]);

    return (
        <Modal
            title={`Gérer les abonnements de ${companyDoc.name}`}
            open={modalProps.open}
            onCancel={modalProps.onCancel}
            onOk={() => void handleSubmit()}
            okText={modalProps.okText}
            cancelText={modalProps.cancelText}
            width={modalProps.width}
            confirmLoading={isSubmitting}
            okButtonProps={{ loading: isSubmitting }}
            destroyOnHidden
        >
            <Flex vertical gap={12}>
                <Alert closable showIcon message="La modification des abonnements ne va pas impacter les entreprises déjà liées à ceux-ci. N'utilisez cette fonctionnalité que si vous sachez." type="warning" />

                <Input
                    placeholder="Rechercher un abonnement"
                    allowClear
                    value={queryDraft}
                    onChange={(e) => {
                        setQueryDraft(e.target.value);
                        setCatalogPage(0);
                        setCompanyPage(1);
                    }}
                />

                <Transfer
                    className={styles.companyManageSubscriptionsTransfer}
                    dataSource={dataSource}
                    targetKeys={catalogTargetKeys}
                    selectedKeys={selectedKeys}
                    onChange={handleTransferChange}
                    onSelectChange={(sourceSelectedKeys, targetSelectedKeys) => {
                        setSelectedKeys([
                            ...(sourceSelectedKeys as string[]),
                            ...(targetSelectedKeys as string[]),
                        ]);
                    }}
                    render={(item) => item.title as string}
                    showSearch={false}
                    titles={transferTitles}
                    footer={({ direction }) => (
                        <div className={styles.companyManageSubscriptionsFooter}>
                            {direction === 'left' && companyFilteredCount > 0 ? (
                                <Pagination
                                    size="small"
                                    current={companyPage}
                                    pageSize={companyPageSize}
                                    total={companyFilteredCount}
                                    showSizeChanger
                                    pageSizeOptions={[10, 20, 50]}
                                    onChange={(nextPage, nextPageSize) => {
                                        setCompanyPage(nextPage);
                                        if (nextPageSize !== companyPageSize) {
                                            setCompanyPageSize(nextPageSize);
                                            setCompanyPage(1);
                                        }
                                    }}
                                />
                            ) : <Pagination
                                size="small"
                                current={catalogPage + 1}
                                pageSize={catalogPageSize}
                                total={catalogNbHits}
                                showSizeChanger
                                pageSizeOptions={[10, 20, 50]}
                                onChange={(nextPage1Based, nextPageSize) => {
                                    setCatalogPage(nextPage1Based - 1);
                                    if (nextPageSize !== catalogPageSize) {
                                        setCatalogPageSize(nextPageSize);
                                        setCatalogPage(0);
                                    }
                                }}
                            />}
                        </div>
                    )}
                >
                    {({
                        direction,
                        filteredItems,
                        onItemSelect,
                        onItemSelectAll,
                        selectedKeys: listSelectedKeys,
                        disabled: listDisabled,
                    }) => {
                        const isCompany = direction === 'left';
                        const isCatalog = direction === 'right';
                        const tableItems = isCompany ? companyPageItems : filteredItems;

                        const rowSelection: TableRowSelection<TransferItem> = {
                            getCheckboxProps: () => ({ disabled: listDisabled }),
                            onChange(selectedRowKeys) {
                                if (isCompany) {
                                    const pageItemKeys = tableItems.map((item) => item.key);
                                    const selectedOnOtherPages = listSelectedKeys.filter(
                                        (k) => !pageItemKeys.includes(k)
                                    );
                                    onItemSelectAll(
                                        [...selectedOnOtherPages, ...(selectedRowKeys as Key[])],
                                        'replace'
                                    );
                                    return;
                                }
                                onItemSelectAll(selectedRowKeys, 'replace');
                            },
                            selectedRowKeys: listSelectedKeys,
                            selections: false,
                        };

                        return (
                            <Flex vertical gap={8} className={styles.companyManageSubscriptionsTableWrap}>
                                <Table
                                    rowSelection={rowSelection}
                                    columns={[{
                                        title: 'Nom',
                                        dataIndex: 'title',
                                        key: 'title',
                                    },
                                    ]}
                                    dataSource={tableItems}
                                    size="small"
                                    showHeader={false}
                                    loading={isCompany ? isCompanyLoading : isCatalogLoading}
                                    pagination={false}
                                    style={{ pointerEvents: listDisabled ? 'none' : undefined }}
                                    rowClassName={styles.companyManageSubscriptionsTableRow}
                                    onRow={({ key, disabled: itemDisabled }) => ({
                                        onClick: () => {
                                            if (itemDisabled || listDisabled) return;
                                            onItemSelect(key as Key, !listSelectedKeys.includes(key as Key));
                                        },
                                    })}
                                />
                            </Flex>
                        );
                    }}
                </Transfer>
            </Flex>
        </Modal>
    );
}
